import type { NetworkTraceResult } from "../model/inp/networkTrace.ts";
import type { TraceDirection } from "./useNetworkTrace.ts";
import type { useResultsAnimation } from "./useResultsAnimation.ts";
import type { useThematicMap } from "./useThematicMap.ts";
import type { useMapQuery } from "./useMapQuery.ts";
import type { useNetworkTrace } from "./useNetworkTrace.ts";

/**
 * Results animation, thematic map, map query, and network trace are one
 * mutually-exclusive family of map overlays — opening/arming any one of them
 * always closes the other three, enforced here instead of separately at
 * each of the 5 toolbar call sites that used to repeat the same 3-4 line
 * "close every sibling" block.
 */
export function useExclusiveMapOverlays(
  resultsAnimation: ReturnType<typeof useResultsAnimation>,
  thematicMap: ReturnType<typeof useThematicMap>,
  mapQuery: ReturnType<typeof useMapQuery>,
  networkTrace: ReturnType<typeof useNetworkTrace>
) {
  function toggleResultsAnimation() {
    resultsAnimation.toggle();
    thematicMap.close();
    mapQuery.close();
    networkTrace.clear();
  }

  function toggleThematicMap() {
    thematicMap.toggle();
    resultsAnimation.close();
    mapQuery.close();
    networkTrace.clear();
  }

  function toggleMapQuery() {
    mapQuery.toggle();
    resultsAnimation.close();
    thematicMap.close();
    networkTrace.clear();
  }

  function startNetworkTrace(direction: TraceDirection) {
    networkTrace.startPick(direction);
    resultsAnimation.close();
    thematicMap.close();
    mapQuery.close();
  }

  /** Direct-run variant — PropertyPanel's own contextual trace actions for
   *  an already-selected node, so the start node is already known. Same
   *  "close every sibling overlay" contract as the pick-armed
   *  `startNetworkTrace` above (ToolRail's own "Herramientas" menu), just
   *  skipping the pick round trip. */
  function runNetworkTrace(
    startNode: string,
    direction: TraceDirection,
    trace: NetworkTraceResult
  ) {
    networkTrace.runFrom(startNode, direction, trace);
    resultsAnimation.close();
    thematicMap.close();
    mapQuery.close();
  }

  return {
    toggleResultsAnimation,
    toggleThematicMap,
    toggleMapQuery,
    startNetworkTrace,
    runNetworkTrace,
  };
}
